import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faCheckCircle,
  faShieldAlt,
  faTrash,
  faPaperclip,
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileArchive,
  faFileCode,
  faInfoCircle,
  faSearch,
  faShareAlt,
  faEdit,
  faPaperPlane,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ScamDetectorAPI from "../../services/ScamDetectorAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import { useLoading } from "../../../../contexts/LoadingContext";
import { getConfidenceDescription } from "./utils/confidenceUtils";
import RefreshButton from "../../common/RefreshButton";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import {
  decodeQuotedPrintable,
  formatDateOnly,
  formatTimeOnly,
  decodeAndSanitizeHtml,
} from "./utils/emailAnalysisUtils";
import EmailAnalysisAPI from "../../services/EmailAnalysisAPI";
import DisclosuresModal from "../common/DisclosuresModal";
import OreInsightCard from "../common/OreInsightCard";
import HelpGuideButton from "../../HelpGuideButton";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import ReanalyzeWithContextPanel from "../common/ReanalyzeWithContextPanel";
import AnalysisFeedbackBar from "../common/AnalysisFeedbackBar";
import EmailCheckStart from "./components/EmailCheckStart";
import { useSubscription } from "../../context/SubscriptionContext";
import "../../css/Common.css";
import "./css/EmailAnalysisSimpleMobile.css";

// ─────────────────────────────────────────────
// Helper: risk classification
// ─────────────────────────────────────────────
const getRiskClass = (analysis) => {
  if (!analysis?.analysis?.analysisResult) return "unknown-risk";
  const { isScam, confidence } = analysis.analysis.analysisResult;
  if (isScam) {
    if (confidence >= 0.8) return "very-high-risk";
    if (confidence >= 0.6) return "high-risk";
    return "medium-risk";
  }
  return "low-risk";
};

const getRiskIcon = (analysis) => {
  const cls = getRiskClass(analysis);
  return cls === "low-risk" ? faCheckCircle : faExclamationTriangle;
};

// ─────────────────────────────────────────────
// Helper: file type icon for attachments
// ─────────────────────────────────────────────
const getFileIcon = (filename) => {
  if (!filename) return faFileAlt;
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf") return faFilePdf;
  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(ext))
    return faFileImage;
  if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return faFileArchive;
  if (
    ["js", "py", "html", "css", "java", "exe", "bat", "sh", "ps1"].includes(ext)
  )
    return faFileCode;
  return faFileAlt;
};

const getAttachmentRisk = (filename) => {
  if (!filename) return "low";
  const ext = filename.split(".").pop().toLowerCase();
  if (["exe", "bat", "cmd", "ps1", "vbs", "js", "scr", "msi"].includes(ext))
    return "high";
  if (["zip", "rar", "7z", "doc", "docm", "xlsm", "pptm"].includes(ext))
    return "medium";
  return "low";
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Main Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const EmailAnalysisSimpleMobile = ({
  selectedAnalysis,
  analyses,
  handleAnalysisSelected,
  handleReanalyzeWithContext,
  refreshAnalyses,
  userUuid,
  setError,
  activeTab,
  setActiveTab,
  onAnalysisDeleted,
  reportIdMap = {},
  onNavigateToManual,
  userEmail,
  awaitState,
  onStartAwaiting,
  onCancelAwaiting,
  forwardingDisabled,
  hasLoaded = true,
}) => {
  // ── Local state ──
  const [viewMode, setViewMode] = useState("list"); // "list" | "detail" | "manual"

  // ── Manual entry state ──
  const { showLoading, hideLoading } = useLoading();
  const [manualEmailContent, setManualEmailContent] = useState({
    senderEmail: "",
    subject: "",
    body: "",
  });
  const [manualUserContext, setManualUserContext] = useState("");
  const [manualResult, setManualResult] = useState(null);
  const [manualError, setManualError] = useState(null);
  const [manualActiveSlide, setManualActiveSlide] = useState("form"); // "form" | "results"
  const [expandedSections, setExpandedSections] = useState({
    suggestions: true,
    riskFactors: false,
    reasoning: true,
    attachments: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localAnalyses, setLocalAnalyses] = useState([]);
  const share = useShareAlert();
  const { t } = useTranslation();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const detailRef = useRef(null);
  const slideDetailRef = useRef(null);
  const reanalyzeRef = useRef(null);

  const scrollToReanalyze = useCallback(() => {
    if (reanalyzeRef.current) {
      reanalyzeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  // ── Derive sorted list (latest 5) ──
  useEffect(() => {
    if (!analyses || analyses.length === 0) {
      setLocalAnalyses([]);
      return;
    }
    const sorted = [...analyses].sort((a, b) => {
      const dateA =
        a.analysis?.analysisResult?.analysisDate ||
        a.lastModified ||
        new Date(0);
      const dateB =
        b.analysis?.analysisResult?.analysisDate ||
        b.lastModified ||
        new Date(0);
      return new Date(dateB) - new Date(dateA);
    });
    setLocalAnalyses(sorted.slice(0, 5));
  }, [analyses]);

  // ── Sync viewMode with parent activeTab ──
  useEffect(() => {
    if (activeTab === "details" && selectedAnalysis) {
      setViewMode("detail");
    }
  }, [activeTab, selectedAnalysis]);

  // ── When entering detail or manual view, scroll to top ──
  useEffect(() => {
    if (viewMode === "detail" || viewMode === "manual") {
      // Scroll the .hero ancestor (the actual scroll container on mobile)
      const heroEl = document.querySelector(".hero");
      if (heroEl) {
        heroEl.scrollTop = 0;
      }
      window.scrollTo(0, 0);
      if (slideDetailRef.current) {
        slideDetailRef.current.scrollTop = 0;
      }
      if (detailRef.current) {
        detailRef.current.scrollTop = 0;
      }
    }
  }, [viewMode, selectedAnalysis]);

  // ── Handlers ──
  const handleCardTap = useCallback(
    (analysis) => {
      handleAnalysisSelected(analysis);
      setViewMode("detail");
      setActiveTab("details");
      setExpandedSections({
        suggestions: true,
        riskFactors: false,
        reasoning: true,
        attachments: false,
      });
    },
    [handleAnalysisSelected, setActiveTab],
  );

  const handleBack = useCallback(() => {
    setViewMode("list");
    setActiveTab("results");
  }, [setActiveTab]);

  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualEmailContent((prev) => ({ ...prev, [name]: value }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    showLoading(t("manual.loading.title"), t("manual.loading.subtitle"));
    setManualError(null);
    setManualResult(null);
    try {
      const contentWithContext = { ...manualEmailContent };
      if (manualUserContext.trim()) {
        contentWithContext.userContext = manualUserContext.trim();
      }
      const result = await ScamDetectorAPI.analyzeEmail(contentWithContext);
      setManualResult(result);
      setManualActiveSlide("results");
      if (result.isScam === true) {
        try {
          await ScamReportAPI.saveReport({
            analysisType: "EMAIL_ANALYSIS",
            analysisTitle: manualEmailContent.subject || "Email Scam Detected",
            analysisDetail:
              "This email contains suspicious or fraudulent content",
            analysisInput: manualEmailContent.body || "",
            context: manualUserContext?.trim() || undefined,
            isScam: true,
            confidence: result.confidence || 0,
            riskLevel: result.riskLevel || "high",
            explanation: result.explanation || "",
            suggestions: result.suggestions || "",
          });
        } catch (saveErr) {
          console.error(
            "[EmailAnalysisSimpleMobile] Failed to save report:",
            saveErr,
          );
        }
      }
    } catch (err) {
      console.error("[EmailAnalysisSimpleMobile] Manual analysis error:", err);
      setManualError(err.message || t("manual.error.generic"));
    } finally {
      hideLoading();
    }
  };

  const handleManualReset = () => {
    setManualEmailContent({ senderEmail: "", subject: "", body: "" });
    setManualUserContext("");
    setManualResult(null);
    setManualError(null);
    setManualActiveSlide("form");
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshAnalyses();
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshAnalyses]);

  const handleDelete = useCallback(
    async (e, analysisItem) => {
      e.stopPropagation();
      try {
        await EmailAnalysisAPI.deleteAnalysis(analysisItem.key);
        if (selectedAnalysis && selectedAnalysis.key === analysisItem.key) {
          handleAnalysisSelected(null);
          setViewMode("list");
          setActiveTab("results");
        }
        onAnalysisDeleted(analysisItem.key);
      } catch {
        setError(t("email.error.deleteFailed"));
      }
    },
    [
      selectedAnalysis,
      handleAnalysisSelected,
      setActiveTab,
      setError,
      onAnalysisDeleted,
    ],
  );

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: Results List View
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderListView = () => (
    <div className="easm-list-view">
      {/* Header */}
      <div className="easm-header">
        <div className="easm-header-top">
          <h1 className="main-title">{t("email.page.title")}</h1>
          {localAnalyses.length > 0 && <HelpGuideButton guideId={1} />}
        </div>
        <p className="common-subtitle">{t("email.page.subtitle")}</p>
      </div>

      {hasLoaded && (
        <EmailCheckStart
          variant={localAnalyses.length > 0 ? "compact" : "full"}
          userEmail={userEmail}
          awaitState={awaitState}
          onStartAwaiting={onStartAwaiting}
          onCancelAwaiting={onCancelAwaiting}
          onPasteInstead={() => setViewMode("manual")}
          forwardingDisabled={forwardingDisabled}
        />
      )}

      {localAnalyses.length > 0 && (
        <>
          {/* Results header */}
          <div className="easm-results-header">
            <h2 className="easm-results-title">
              {t("email.results.heading")}
              <span className="easm-results-count">{localAnalyses.length}</span>
            </h2>
            <RefreshButton
              onClick={handleRefresh}
              loading={isRefreshing}
              size="sm"
            />
          </div>

          <div className="easm-card-list">
            {localAnalyses.map((item) => {
              const result = item.analysis?.analysisResult;
              const isScam = result?.isScam;
              const riskClass = getRiskClass(item);
              const isSelected = selectedAnalysis?.key === item.key;
              const sender = result?.sender || "Unknown";
              const subject = result?.subject
                ? decodeQuotedPrintable(result.subject)
                : "No subject";
              const date = formatDateOnly(
                result?.analysisDate || item.lastModified,
              );
              const time = formatTimeOnly(
                result?.analysisDate || item.lastModified,
              );
              const confidence = getConfidenceDescription(
                result?.confidence,
                result?.isScam,
              );

              return (
                <button
                  key={item.key}
                  className={`easm-card ${riskClass} ${isSelected ? "selected" : ""}`}
                  onClick={() => handleCardTap(item)}
                  type="button"
                >
                  <div className="easm-card-risk-strip" />
                  <div className="easm-card-body">
                    <div className="easm-card-top">
                      <span className={`easm-card-badge ${riskClass}`}>
                        <FontAwesomeIcon icon={getRiskIcon(item)} />
                        {isScam ? t("email.badge.scam") : t("email.badge.safe")}
                      </span>
                      <button
                        className="easm-card-delete"
                        onClick={(e) => handleDelete(e, item)}
                        title={t("email.card.deleteTitle")}
                        type="button"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    <div className="easm-card-from">
                      <span className="easm-card-label">
                        {t("email.card.from")}
                      </span>
                      <span className="easm-card-value">{sender}</span>
                    </div>
                    <div className="easm-card-subject">
                      <span className="easm-card-label">
                        {t("email.card.subject")}
                      </span>
                      <span className="easm-card-value">{subject}</span>
                    </div>
                    <div className="easm-card-footer">
                      <span className="easm-card-confidence">{confidence}</span>
                      <span className="easm-card-date">
                        {date} {time}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <DisclosuresModal disclaimerText={t("email.disclaimer")} />
    </div>
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: Detail View
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderDetailView = () => {
    if (!selectedAnalysis) {
      handleBack();
      return null;
    }

    const result = selectedAnalysis.analysis?.analysisResult;
    const isScam = result?.isScam;
    const confidence = result?.confidence || 0;
    const suggestions = result?.suggestions || "";
    const suspiciousElements = result?.suspiciousElements || [];
    const reasoning = result?.reasoning || "";

    // Attachment data
    const attachmentNames =
      selectedAnalysis.analysis?.analyzedEmailContent?.attachmentNames ||
      selectedAnalysis.analysisResult?.analyzedEmailContent?.attachmentNames ||
      [];
    const existingContext =
      selectedAnalysis?.analysis?.analysisResult?.htmlEmailContent
        ?.userContext ||
      selectedAnalysis?.analysis?.analysisResult?.analyzedEmailContent
        ?.userContext ||
      "";

    return (
      <div className="easm-detail-view" ref={detailRef}>
        {/* Back button */}
        <button className="easm-back-btn" onClick={handleBack} type="button">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t("email.detail.back")}</span>
        </button>

        {/* Threat Assessment Banner (desktop style) */}
        <div className="case-details">
          <div
            className={`threat-banner easm-threat-banner ${isScam ? "threat-detected" : confidence <= 0.2 ? "security-failed" : "safe"}`}
          >
            <div className="threat-icon">
              <FontAwesomeIcon
                icon={isScam ? faExclamationTriangle : faCheckCircle}
              />
            </div>
            <h2 className="threat-title">
              {isScam
                ? t("email.detail.scamDetected")
                : t("email.detail.noThreats")}
            </h2>
            {isScam && (
              <button
                className="threat-banner-share-btn"
                onClick={
                  isIndividualPlan
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        share.toggleShare();
                      }
                }
                disabled={isIndividualPlan}
                title={
                  isIndividualPlan
                    ? t("email.detail.shareIndividual")
                    : t("email.detail.shareFamily")
                }
              >
                <FontAwesomeIcon
                  icon={faShareAlt}
                  className="threat-share-icon"
                />
                <span className="threat-share-label">
                  {t("email.detail.share")}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Inline Share Form */}
        {share.shareOpen && isScam && (
          <InlineShareForm
            shareSuccess={share.shareSuccess}
            shareError={share.shareError}
            shareMessage={share.shareMessage}
            setShareMessage={share.setShareMessage}
            shareLoading={share.shareLoading}
            onCancel={share.closeShare}
            onSend={() =>
              share.sendShare(
                selectedAnalysis?.reportId ||
                  reportIdMap[selectedAnalysis?.key],
              )
            }
          />
        )}

        {suggestions && (
          <div className="suggestions-section">
            <h4 className="subsection-title">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="fa-icon-margin-right-8"
              />
              {t("email.section.whatToDo")}
            </h4>
            <SuggestionsBulletList text={suggestions} />
          </div>
        )}

        {/* Risk Factors (single panel) */}
        {suspiciousElements.length > 0 && (
          <div className="risk-factors-section easm-risk-panel">
            <h4 className="subsection-title">
              <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
              {t("email.section.riskFactors")} ({suspiciousElements.length})
            </h4>
            <ul className="easm-risk-list">
              {suspiciousElements.map((factor, i) => (
                <li key={i} className="easm-risk-item">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="easm-risk-item-icon"
                  />
                  <span>{factor}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Explanation section (desktop style) */}
        {reasoning && (
          <div className="explanation-section">
            <h4
              className="subsection-title collapsible-header"
              onClick={() => toggleSection("reasoning")}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                className="fa-icon-margin-right-8"
              />
              {t("email.section.explanation")}
              <FontAwesomeIcon
                icon={expandedSections.reasoning ? faChevronUp : faChevronDown}
                style={{ marginLeft: "auto", fontSize: "0.85rem" }}
              />
            </h4>
            {expandedSections.reasoning && (
              <div
                className="explanation-text"
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(reasoning || ""),
                }}
              />
            )}
          </div>
        )}

        {/* Feedback bar */}
        <AnalysisFeedbackBar
          analysisKey={selectedAnalysis?.key}
          onNegativeFeedback={scrollToReanalyze}
        />

        {/* Re-analyze with Context */}
        <div ref={reanalyzeRef}>
          <ReanalyzeWithContextPanel
            initialContext={existingContext}
            onSubmit={(context) =>
              handleReanalyzeWithContext?.(selectedAnalysis, context)
            }
          />
        </div>

        <div className="easm-sections">
          {/* Organization Verification */}
          <OreInsightCard
            oreData={selectedAnalysis?.analysis?.analysisResult}
            channel="email"
            isMobile={true}
            accordionPrefix="easm"
          />

          {/* Attachments */}
          {attachmentNames.length > 0 && (
            <div
              className={`easm-accordion ${expandedSections.attachments ? "open" : ""}`}
            >
              <button
                className="easm-accordion-header"
                onClick={() => toggleSection("attachments")}
                type="button"
              >
                <span className="easm-accordion-title">
                  <FontAwesomeIcon icon={faPaperclip} />
                  {t("email.section.attachments")}
                  <span className="easm-accordion-count">
                    {attachmentNames.length}
                  </span>
                </span>
                <FontAwesomeIcon
                  icon={
                    expandedSections.attachments ? faChevronUp : faChevronDown
                  }
                />
              </button>
              {expandedSections.attachments && (
                <div className="easm-accordion-body">
                  <div className="easm-attachment-list">
                    {attachmentNames.map((name, i) => {
                      const risk = getAttachmentRisk(name);
                      return (
                        <div
                          key={i}
                          className={`easm-attachment-item easm-attachment-${risk}`}
                        >
                          <FontAwesomeIcon
                            icon={getFileIcon(name)}
                            className="easm-attachment-icon"
                          />
                          <span className="easm-attachment-name">{name}</span>
                          <span className={`easm-attachment-risk ${risk}`}>
                            {t(`email.attachment.risk.${risk}`)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MANUAL ENTRY VIEW
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderManualView = () => {
    const hasFormContent =
      manualEmailContent.senderEmail ||
      manualEmailContent.subject ||
      manualEmailContent.body;

    if (manualActiveSlide === "results" && manualResult) {
      return (
        <div className="easm-manual-view">
          <button
            type="button"
            className="easm-back-btn"
            onClick={() => setManualActiveSlide("form")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {t("manual.results.backToForm")}
          </button>
          <div className="easm-manual-result">
            <div
              className={`threat-banner ${manualResult.isScam ? "threat-detected" : "safe"}`}
            >
              <div className="threat-icon">
                <FontAwesomeIcon
                  icon={
                    manualResult.isScam ? faExclamationTriangle : faCheckCircle
                  }
                />
              </div>
              <h2 className="threat-title">
                {manualResult.isScam
                  ? t("email.detail.scamDetected")
                  : t("manual.results.appearsLegitimate")}
              </h2>
            </div>
            {manualResult.explanation && (
              <div className="easm-manual-result-section">
                <strong>{t("email.detail.explanation")}</strong>
                <p>{manualResult.explanation}</p>
              </div>
            )}
            {manualResult.suggestions && (
              <div className="easm-manual-result-section">
                <strong>{t("email.detail.suggestions")}</strong>
                <SuggestionsBulletList suggestions={manualResult.suggestions} />
              </div>
            )}
            <button
              type="button"
              className="btn btn-ghost easm-manual-analyze-again-btn"
              onClick={handleManualReset}
            >
              {t("manual.form.analyzeBtn")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="easm-manual-view">
        <button
          type="button"
          className="easm-back-btn"
          onClick={() => {
            handleManualReset();
            setViewMode("list");
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t("email.mobile.backToList")}
        </button>

        <h1 className="main-title">{t("manual.page.title")}</h1>
        <p className="common-subtitle">{t("manual.page.subtitle")}</p>

        {manualError && (
          <div className="easm-error-banner">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{manualError}</span>
          </div>
        )}

        <form onSubmit={handleManualSubmit} className="easm-manual-form">
          <div className="easm-manual-field">
            <label htmlFor="easm-man-senderEmail">
              {t("manual.form.senderLabel")}
            </label>
            <input
              type="text"
              id="easm-man-senderEmail"
              name="senderEmail"
              value={manualEmailContent.senderEmail}
              onChange={handleManualInputChange}
              placeholder="example@domain.com"
              required
            />
          </div>
          <div className="easm-manual-field">
            <label htmlFor="easm-man-subject">
              {t("manual.form.subjectLabel")}
            </label>
            <input
              type="text"
              id="easm-man-subject"
              name="subject"
              value={manualEmailContent.subject}
              onChange={handleManualInputChange}
              placeholder="Email subject line"
              required
            />
          </div>
          <div className="easm-manual-field">
            <label htmlFor="easm-man-body">{t("manual.form.bodyLabel")}</label>
            <textarea
              id="easm-man-body"
              name="body"
              value={manualEmailContent.body}
              onChange={handleManualInputChange}
              placeholder={t("manual.form.bodyPlaceholder")}
              rows={6}
              required
            />
          </div>
          <div className="easm-manual-field">
            <label htmlFor="easm-man-context">
              <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
              {t("manual.form.contextLabel")}
            </label>
            <textarea
              id="easm-man-context"
              value={manualUserContext}
              onChange={(e) => setManualUserContext(e.target.value)}
              placeholder={t("manual.form.contextPlaceholder")}
              rows={3}
            />
          </div>
          <div className="btn-group">
            <button type="submit" className="btn btn-primary">
              <FontAwesomeIcon icon={faPaperPlane} />
              {t("manual.form.analyzeBtn")}
            </button>
            {hasFormContent && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleManualReset}
              >
                <FontAwesomeIcon icon={faTimes} />
                {t("manual.form.clearBtn")}
              </button>
            )}
          </div>
        </form>

        {manualResult && (
          <button
            type="button"
            className="btn btn-secondary easm-manual-view-results-link"
            onClick={() => setManualActiveSlide("results")}
          >
            {t("manual.results.viewResults")}
          </button>
        )}
      </div>
    );
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // MAIN RENDER
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  return (
    <div className="easm-container">
      <div
        className={`easm-view-slider ${viewMode === "detail" ? "easm-show-detail" : viewMode === "manual" ? "easm-show-manual" : ""}`}
      >
        <div className="easm-slide easm-slide-list">{renderListView()}</div>
        <div className="easm-slide easm-slide-detail" ref={slideDetailRef}>
          {viewMode === "detail" && renderDetailView()}
        </div>
        <div className="easm-slide easm-slide-manual">
          {viewMode === "manual" && renderManualView()}
        </div>
      </div>
    </div>
  );
};

export default EmailAnalysisSimpleMobile;
